import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import InvoiceSearch from "../../Empresa/NuevaFactura/InvoiceProduct/InvoiceSearch";

const toInt = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : 0;
};

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const money = (v) => `$${toNum(v).toFixed(2)}`;

const calcFinal = (mov, actual, qty) => {
  const a = toInt(actual);
  const q = toInt(qty);
  return mov === "SALIDA" ? a - q : a + q;
};

const newRow = () => ({
  key: crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
  producto_id: "",
  codigo: "",
  nombre: "",
  stock_actual: 0,
  cantidad: "1",
  costo_unitario: "0",
});

const InventoryProductsInline = ({ movimiento, rows, setRows, onChange }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const [errorMessage, setErrorMessage] = useState("");

  const itemsPayload = useMemo(() => {
    return safeRows
      .filter((r) => String(r.producto_id || "").trim())
      .map((r) => {
        const stock_actual = toInt(r.stock_actual);
        const cantidad = toInt(r.cantidad);
        const stock_final = calcFinal(movimiento, stock_actual, cantidad);
        const costo_unitario = toNum(r.costo_unitario);
        const total_costo = cantidad * costo_unitario;

        return {
          producto_id: r.producto_id,
          codigo: r.codigo || "",
          nombre: r.nombre || "",
          cantidad,
          stock_actual,
          stock_final,
          costo_unitario,
          total_costo,
          movimiento,
        };
      });
  }, [safeRows, movimiento]);

  useEffect(() => {
    onChange?.((prev) => ({
      ...(prev || {}),
      inventario_tipo: movimiento,
      items: itemsPayload,
    }));
  }, [itemsPayload, movimiento, onChange]);

  const totalCosto = useMemo(() => {
    return safeRows.reduce((acc, r) => acc + toInt(r.cantidad) * toNum(r.costo_unitario), 0);
  }, [safeRows]);

  const handleAddRow = () => {
    setRows((prev) => [...(Array.isArray(prev) ? prev : []), newRow()]);
    setErrorMessage("");
  };

  const handleDelete = (idx) => {
    setRows((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.filter((_, i) => i !== idx);
    });
    setErrorMessage("");
  };

  const handleQtyChange = (idx, value) => {
    if (value === "" || /^\d+$/.test(value)) {
      setRows((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        return arr.map((r, i) => (i === idx ? { ...r, cantidad: value } : r));
      });
    }
  };

  const handleCostChange = (idx, value) => {
    if (value === "" || /^\d+([.,]\d{0,4})?$/.test(value)) {
      setRows((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        return arr.map((r, i) => (i === idx ? { ...r, costo_unitario: value } : r));
      });
    }
  };

  const handleSelect = (idx, p) => {
    if (!p?.id) return;

    const exists = safeRows.some((r, i) => i !== idx && String(r.producto_id) === String(p.id));
    if (exists) {
      setErrorMessage("Ese producto ya está agregado.");
      return;
    }

    setErrorMessage("");

    setRows((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map((r, i) =>
        i === idx
          ? {
              ...r,
              producto_id: p.id,
              codigo: p.code || "",
              nombre: (p.name || "").split(" (")[0],
              stock_actual: toInt(p.stock),
            }
          : r
      );
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          sx={{
            textTransform: "none",
            bgcolor: "#1565C0",
            px: 2.5,
            py: 1.1,
            borderRadius: 1.5,
            boxShadow: "0 3px 8px rgba(0,0,0,0.18)",
            "&:hover": { bgcolor: "#0f4fa0" },
          }}
        >
          Agregar producto
        </Button>
        <Box />
      </Box>

      {!!errorMessage && (
        <Typography color="error" sx={{ mb: 2, fontWeight: 600 }}>
          {errorMessage}
        </Typography>
      )}

      <Table size="small" sx={{ "& td, & th": { verticalAlign: "middle" } }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#1565C0" }}>
            <TableCell sx={{ color: "#fff", width: 52, textAlign: "center" }} />
            <TableCell sx={{ color: "#fff", width: 120, textAlign: "center" }}>Cantidad</TableCell>
            <TableCell sx={{ color: "#fff", minWidth: 420 }}>Producto</TableCell>
            <TableCell sx={{ color: "#fff", width: 150, textAlign: "center" }}>Stock actual</TableCell>
            <TableCell sx={{ color: "#fff", width: 150, textAlign: "center" }}>Stock final</TableCell>
            <TableCell sx={{ color: "#fff", width: 150, textAlign: "center" }}>Costo unitario</TableCell>
            <TableCell sx={{ color: "#fff", width: 140, textAlign: "center" }}>Total</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {safeRows.map((r, i) => {
            const hasProduct = String(r.producto_id || "").trim();
            const stockFinal = hasProduct ? calcFinal(movimiento, r.stock_actual, r.cantidad) : "";
            const invalidSalida = movimiento === "SALIDA" && hasProduct && Number(stockFinal) < 0;
            const totalRow = hasProduct ? toInt(r.cantidad) * toNum(r.costo_unitario) : 0;

            return (
              <TableRow key={r.key || String(i)} sx={{ "& td": { py: 1.2 } }}>
                <TableCell sx={{ textAlign: "center" }}>
                  <IconButton onClick={() => handleDelete(i)} size="small">
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>

                <TableCell sx={{ textAlign: "center" }}>
                  <TextField
                    value={String(r.cantidad ?? "")}
                    onChange={(e) => handleQtyChange(i, e.target.value)}
                    size="small"
                    error={invalidSalida}
                    inputProps={{ inputMode: "numeric", style: { textAlign: "center" } }}
                    sx={{ width: 80, "& .MuiOutlinedInput-root": { height: 40 } }}
                  />
                </TableCell>

                <TableCell sx={{ minWidth: 420 }}>
                  <Box sx={{ display: "flex", alignItems: "center", "& .MuiOutlinedInput-root": { height: 40 } }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <InvoiceSearch onSelect={(p) => handleSelect(i, p)} placeholder="Buscar producto..." />
                    </Box>
                  </Box>
                </TableCell>

                <TableCell sx={{ textAlign: "center" }}>
                  <TextField
                    value={String(toInt(r.stock_actual))}
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ width: 90, "& .MuiOutlinedInput-root": { height: 40 } }}
                    inputProps={{ style: { textAlign: "center" } }}
                  />
                </TableCell>

                <TableCell sx={{ textAlign: "center" }}>
                  <TextField
                    value={String(stockFinal)}
                    size="small"
                    InputProps={{ readOnly: true }}
                    error={invalidSalida}
                    sx={{ width: 90, "& .MuiOutlinedInput-root": { height: 40 } }}
                    inputProps={{ style: { textAlign: "center" } }}
                  />
                </TableCell>

                <TableCell sx={{ textAlign: "center" }}>
                  <TextField
                    value={String(r.costo_unitario ?? "")}
                    onChange={(e) => handleCostChange(i, e.target.value)}
                    size="small"
                    inputProps={{ inputMode: "decimal", style: { textAlign: "center" } }}
                    sx={{ width: 90, "& .MuiOutlinedInput-root": { height: 40 } }}
                  />
                </TableCell>

                <TableCell sx={{ textAlign: "center" }}>
                  <TextField
                    value={money(totalRow)}
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ width: 110, "& .MuiOutlinedInput-root": { height: 40 } }}
                    inputProps={{ style: { textAlign: "center", fontWeight: 700 } }}
                  />
                </TableCell>
              </TableRow>
            );
          })}

          <TableRow>
            <TableCell colSpan={5} />
            <TableCell sx={{ fontWeight: 800, textAlign: "right", pr: 2 }}>Total:</TableCell>
            <TableCell sx={{ fontWeight: 800, textAlign: "center" }}>{money(totalCosto)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const InventoryDetail = ({ inventarioTipo, onMotionDataChange, productRows, setProductRows }) => {
  const [tabValue, setTabValue] = useState(0);
  const mov = String(inventarioTipo || "INGRESO").toUpperCase();

  return (
    <Box sx={{ mx: "auto", mt: 3 }}>
      <Paper
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{
            backgroundColor: "#1565C0",
            "& .MuiTab-root": {
              color: "#fff",
              textTransform: "none",
              fontWeight: 700,
              minHeight: "48px",
            },
            "& .Mui-selected": {
              backgroundColor: "#fff",
              color: "#1565C0",
              borderTop: "4px solid #1565C0",
              borderLeft: "4px solid #1565C0",
              borderRight: "4px solid #1565C0",
              borderBottom: "none",
              marginTop: "5px",
              marginLeft: "10px",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            },
            "& .MuiTabs-indicator": { display: "none" },
          }}
        >
          <Tab label="Productos" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && (
            <InventoryProductsInline
              movimiento={mov}
              rows={productRows}
              setRows={setProductRows}
              onChange={onMotionDataChange}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default InventoryDetail;
